import SharedDialog from '../../../../shared/shared-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, deleteDriveDocs } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { Doc } from '../../../../../types';
import { useState } from 'react';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import Typography from '@mui/material/Typography';

interface DriveDeleteDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedDocs: Doc[];
  driveId: string;
  changeLogRoles: string[];
  isAllUsers?: boolean;
}

export default function DriveDeleteDialog({
  open,
  handleClose,
  userEmail,
  selectedDocs,
  driveId,
  changeLogRoles,
  isAllUsers,
}: DriveDeleteDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteDriveDocs(userEmail, selectedDocs, changeLogMessage),
    onSuccess: () => {
      if (selectedDocs.length > 25) {
        setSnackbar({
          open: true,
          severity: 'info',
          message:
            'The task to permanently delete the documents or folders has started.',
        });
      } else {
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'The documents or folders were deleted successfully!',
        });

        // update docs of trash folder
        queryClient.invalidateQueries({
          queryKey: [UserKeys.DriveDocuments, userEmail, `${driveId}.trash`],
        });

        // update docs of trash folder
        queryClient.invalidateQueries({
          queryKey: [UserKeys.DriveFolders, userEmail, `${driveId}.trash`],
        });
      }

      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue deleting the selected documents or folders. Please refresh and try again.',
      });
    },
    onSettled: () => {
      if (isAllUsers) {
        createEvent(analytics.drive.drive_explorer.delete_drive_content, {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.driveExplorer,
        });
      } else {
        createEvent(
          analytics.user_management.user_details.drive.delete_drive_content,
          {
            module: ModuleList.modules.userManagement.name,
            section: ModuleList.modules.userManagement.sections.drive,
          }
        );
      }
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanDeleteDocs.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Permanently Delete"
      actingAs={userEmail}
      dialogContent={
        <>
          <Typography>
            {selectedDocs.length === 1
              ? `Do you wish to permanently delete the ${
                  selectedDocs[0].mimeType.includes('folder')
                    ? 'folder'
                    : 'document'
                }: "${selectedDocs[0].title}"?`
              : `Do you wish to permanently delete the ${selectedDocs.length} selected documents or folders?`}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </>
      }
      primaryButton={{
        title: 'Delete',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
